import React, { lazy, ReactNode, Suspense, useState } from 'react';

import PlusLightIcon from '../../../../shared/icons/plus-light-icon';
import Button from '../../../button/button';
import { EVENTS } from '../../fitment-constants';
import FitmentSubTabs, { Tab } from '../fitment-sub-tabs/fitment-sub-tabs';

import { isArrayWithLength } from '../../../../shared/utils/validators/validators';

import { Fitment } from '../../../../shared/types/fitment';

import './fitment-recent-vehicles.scss';

const FitmentList = lazy(
  () =>
    import(
      /* webpackChunkName: "fitment-list" */
      '../fitment-list/fitment-list'
    )
);

const RECENT_TAB = 'RECENT';
const SAVED_TAB = 'SAVED';

const RECENT_CONTENT = 'recentContent';
const SAVED_CONTENT = 'savedContent';

const TABS = [
  {
    ariaControls: SAVED_CONTENT,
    description: SAVED_TAB
  },
  {
    ariaControls: RECENT_CONTENT,
    description: RECENT_TAB
  }
];

interface FitmentRecentVehiclesProps {
  addVehicleOnClick: () => void;
  canToggleVehicle: boolean;
  hideDelete: boolean;
  isLoggedIn: boolean;
  onEvent: ({ name, payload }) => void;
  onFitmentSelected: () => void;
  onSelectFitment: () => void;
  recentFitments: Fitment[];
  renderSavedFitmentsMessage: (isLoggedIn?: boolean) => ReactNode;
  savedFitments: Fitment[];
  selectedFitment: Fitment;
}

function FitmentRecentVehicles(props: FitmentRecentVehiclesProps) {
  const {
    addVehicleOnClick,
    canToggleVehicle,
    hideDelete,
    isLoggedIn,
    onEvent,
    onFitmentSelected,
    onSelectFitment,
    recentFitments,
    renderSavedFitmentsMessage,
    savedFitments,
    selectedFitment
  } = props;
  const isSelectedFitmentSaved =
    isArrayWithLength(savedFitments) &&
    savedFitments.some(fitment => fitment.uid === selectedFitment?.uid);
  const [selectedTab, setSelectedTab] = useState<Tab>(
    isLoggedIn && (!selectedFitment || isSelectedFitmentSaved)
      ? TABS[0]
      : TABS[1]
  );

  return (
    <>
      <div styleName="tabs-container">
        <FitmentSubTabs
          onTabSelect={selectedTab => {
            onEvent({
              name: EVENTS.VEHICLE_TAB_SELECTED,
              payload: selectedTab
            });
            setSelectedTab(selectedTab);
          }}
          selectedTab={selectedTab}
          tabs={TABS}
        />
        <Button
          onClick={() => {
            onEvent({
              name: EVENTS.VEHICLE_ADD_SELECTED,
              payload: selectedTab
            });
            addVehicleOnClick();
          }}
          size="regular"
          styleName="add-vehicle-button"
        >
          <PlusLightIcon styleName="plus-icon" />
          add vehicle
        </Button>
      </div>

      {selectedTab?.description === SAVED_TAB && (
        <div aria-labelledby="tab-saved" id={SAVED_CONTENT} role="tabpanel">
          {isLoggedIn && (
            <>
              {savedFitments?.length > 0 && (
                <Suspense fallback={null}>
                  <FitmentList
                    canToggleVehicle={canToggleVehicle}
                    fitments={savedFitments.slice(0, 3)}
                    hideDelete={hideDelete}
                    onFitmentRemoved={fitment =>
                      void onEvent({
                        name: EVENTS.VEHICLE_RECENT_FITMENT_REMOVED,
                        payload: fitment
                      })
                    }
                    onFitmentSelected={onFitmentSelected}
                    onSelectFitment={onSelectFitment}
                    selectedFitment={selectedFitment}
                  />
                  {savedFitments.length > 3 && (
                    <div
                      className="fitment-recent-vehicles__show-all-container"
                      styleName="show-all-container"
                    >
                      <Button
                        href="#/fitment/my-vehicles"
                        size="regular"
                        styleName="show-all-button"
                      >
                        show all saved vehicles
                      </Button>
                    </div>
                  )}
                </Suspense>
              )}
            </>
          )}
          {typeof renderSavedFitmentsMessage === 'function' &&
            (!isLoggedIn || !savedFitments?.length) && (
              <div
                className="fitment-recent-vehicles__vehicle-selection-text"
                styleName="vehicle-selection-text"
              >
                {renderSavedFitmentsMessage(isLoggedIn)}
              </div>
            )}
        </div>
      )}

      {selectedTab?.description === RECENT_TAB && (
        <div aria-labelledby="tab-recent" id={RECENT_CONTENT} role="tabpanel">
          {recentFitments?.length > 0 && (
            <Suspense fallback={null}>
              <FitmentList
                canToggleVehicle={canToggleVehicle}
                fitments={recentFitments}
                hideDelete={hideDelete}
                onFitmentRemoved={fitment =>
                  void onEvent({
                    name: EVENTS.VEHICLE_RECENT_FITMENT_REMOVED,
                    payload: fitment
                  })
                }
                onFitmentSelected={onFitmentSelected}
                onSelectFitment={onSelectFitment}
                selectedFitment={selectedFitment}
              />
            </Suspense>
          )}
          {!recentFitments?.length && (
            <div
              className="fitment-recent-vehicles__vehicle-selection-text"
              styleName="vehicle-selection-text"
            >
              You have no recent vehicle searches
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default FitmentRecentVehicles;
